import { useState } from "react";
import { eventPlural, eventWords } from "../../../shared/goals/events";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { DESTS, EVENTS_MANAGER, useWhereOpens, type WhereProps } from "./WhereOpens";

const HTTPS = /^https:\/\/[^\s/]+\.[^\s]+/;

/**
 * Where the ads open on a phone and an iPad, as one sheet: Meta's form or a page on the website, then that
 * choice's own settings and its checks with Meta. Each change saves as it is made, as on a computer.
 */
export function WhereOpensSheet({ open, onClose, ...props }: WhereProps & { open: boolean; onClose: () => void }) {
  const { suggested, suggestedOpens, link, setLink, saveLink, settings } = props;
  const { busy, waited, draft, put, setQ, saveForm, checkPage, submitted, pixel, test, step1, step2, pick, learnable, counted, slow, learn, choose, t } = useWhereOpens(props);
  const [bad, setBad] = useState<string | null>(null);
  const done = () => {
    const v = link.trim();
    if (settings.opens === "site" && v !== (settings.link ?? "")) {
      if (!HTTPS.test(v)) return setBad("The page address starts with https://");
      saveLink();
    }
    if (settings.opens === "metaform") saveForm(draft);
    setBad(null);
    onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={done}
      tall
      title="Where the ads open"
      right={
        <button type="button" className="pn-tbtn strong" onClick={done}>
          Done
        </button>
      }
    >
      <div className="pn-grp" role="radiogroup" aria-label="Where the ads open">
        {DESTS.map((d) => {
          const on = settings.opens === d.key;
          return (
            <button key={d.key} type="button" className="pn-row wrap" role="radio" aria-checked={on} onClick={() => choose(d.key)}>
              <span className="pn-ico">
                <Icon name={d.icon} />
              </span>
              <span className="m">
                <span className="t">
                  {d.name}
                  {d.key === suggestedOpens && <span className="pn-pill acc ad-sugg">Suggested</span>}
                </span>
                <span className="s">{d.line}</span>
              </span>
              <span className={on ? "ad-tick" : "ad-tick off"}>
                <Icon name="check" />
              </span>
            </button>
          );
        })}
      </div>

      {settings.opens === "metaform" && (
        <>
          <div className="pn-cap ad-mt">It asks for name, phone and email, and:</div>
          {draft.questions.map((x, qi) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a question's place is its identity while it is typed.
            <div className="pn-grp ad-q" key={qi}>
              <div className="pn-irow col">
                <label htmlFor={`ad-q${qi}`}>Question {qi + 1}</label>
                <input id={`ad-q${qi}`} value={x.q} onChange={(e) => put(setQ(qi, (q) => ({ ...q, q: e.target.value })))} onBlur={() => saveForm(draft)} />
              </div>
              {x.answers.map((ans, ai) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: an answer's place is its identity while it is typed.
                <div className="pn-irow ad-ans" key={ai}>
                  <input
                    value={ans}
                    aria-label={`Answer ${ai + 1}`}
                    placeholder="An answer"
                    onChange={(e) => put(setQ(qi, (q) => ({ ...q, answers: q.answers.map((v, k) => (k === ai ? e.target.value : v)) })))}
                    onBlur={() => saveForm(draft)}
                  />
                  <button type="button" className="ad-x" aria-label="Remove this answer" onClick={() => put(setQ(qi, (q) => ({ ...q, answers: q.answers.filter((_, k) => k !== ai) })), true)}>
                    <Icon name="x" />
                  </button>
                </div>
              ))}
              <button type="button" className="pn-row accent" onClick={() => put(setQ(qi, (q) => ({ ...q, answers: [...q.answers, ""] })))}>
                <span className="m">
                  <span className="t">{x.answers.length ? "Add an answer" : "Add answers to pick from"}</span>
                  {!x.answers.length && <span className="s">Without them, people type their answer.</span>}
                </span>
              </button>
              <button type="button" className="pn-row danger" onClick={() => put(setQ(qi, () => null), true)}>
                <span className="m">
                  <span className="t">Remove this question</span>
                </span>
              </button>
            </div>
          ))}
          {draft.questions.length < 5 && (
            <div className="pn-grp ad-q">
              <button type="button" className="pn-row accent" onClick={() => put((f) => ({ ...f, questions: [...f.questions, { q: "", answers: [] }] }))}>
                <span className="pn-ico">
                  <Icon name="plus" />
                </span>
                <span className="m">
                  <span className="t">Add a question</span>
                </span>
              </button>
            </div>
          )}
          <div className="pn-grp ad-q">
            <div className="pn-irow col">
              <label htmlFor="ad-privacy">Your privacy policy</label>
              <input id="ad-privacy" inputMode="url" autoComplete="off" value={draft.privacyUrl} placeholder="https://" onChange={(e) => put((f) => ({ ...f, privacyUrl: e.target.value }))} onBlur={() => saveForm(draft)} />
            </div>
          </div>
          <p className="pn-note">Sign-ups arrive in Meta's Leads Center, in Meta Business Suite.</p>
          <div className="pn-cap ad-mt">What Meta aims for</div>
          <div className="pn-grp">
            <div className="pn-row static">
              <span className="m">
                <span className="t">Sign-ups on this form</span>
                <span className="s">Someone submitted this form</span>
              </span>
            </div>
          </div>
        </>
      )}

      {settings.opens === "site" && (
        <>
          <div className="pn-cap ad-mt">The page with your form</div>
          <div className="pn-form">
            <input
              className="pn-inp"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              value={link}
              placeholder="https://"
              aria-label="The page with your form"
              onChange={(e) => {
                setLink(e.target.value);
                setBad(null);
              }}
              onBlur={() => {
                if (HTTPS.test(link.trim())) saveLink();
              }}
            />
          </div>
          {bad ? <p className="pn-note ad-bad">{bad}</p> : <p className="pn-note">Landing here is only a visit. What counts is what they do next, like submitting the form.</p>}
          {settings.link && (
            <>
              <div className="pn-cap ad-mt">Check it with Meta</div>
              <div className="pn-grp">
                <div className="pn-row static wrap ad-chk">
                  <span className={step1 ? "ad-n ok" : "ad-n"}>{step1 ? <Icon name="check" /> : 1}</span>
                  <span className="m">
                    <span className="t">The pixel on this page</span>
                    {pixel?.found && <span className="s ad-okt">Pixel found: {pixel.pixelName ?? "your pixel"}.</span>}
                    {pixel && !pixel.found && pixel.other && (
                      <span className="s ad-vw">
                        This page has another pixel ({pixel.other}), not {pixel.pixelName ?? "yours"}.
                      </span>
                    )}
                    {pixel && !pixel.found && !pixel.other && !test?.event && (
                      <span className="s">{pixel.error ?? `Not in the page itself${pixel.tagManager ? ", which loads a tag manager" : ""}. A plugin or a tag manager may load it: the test sign-up is the proof.`}</span>
                    )}
                    <span className="ad-chkb">
                      <button type="button" className="pn-btn sm" disabled={busy !== null} onClick={() => void checkPage()}>
                        {busy === "page" && <span className="ad-spin" />}
                        Check the page
                      </button>
                    </span>
                  </span>
                </div>
                <div className="pn-row static wrap ad-chk">
                  <span className={step2 ? "ad-n ok" : "ad-n"}>{step2 ? <Icon name="check" /> : 2}</span>
                  <span className="m">
                    <span className="t">Submit one test sign-up</span>
                    {pixel && <span className="s">Fill in the form on the page and submit it.</span>}
                    {test?.event && <span className="s ad-okt">Meta got it, as {test.event}.</span>}
                    {test?.visitsOnly && !test.event && <span className="s ad-vw">Meta saw the visit, but not the sign-up (Lead). Add it, then submit again.</span>}
                    {waited && !test?.event && !test?.visitsOnly && <span className="s ad-vw">Meta has not reported it yet. In Meta Events Manager, select your pixel, then Test events, to see it arrive.</span>}
                    {pixel && (
                      <span className="ad-chkb">
                        <a className="pn-btn sm" href={settings.link} target="_blank" rel="noopener noreferrer">
                          Open the page
                        </a>
                        <button type="button" className="pn-btn sm pri" disabled={busy !== null} onClick={() => void submitted()}>
                          {busy === "test" ? "Waiting for Meta" : "I submitted it"}
                        </button>
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
          {test?.visitsOnly && !test.event && (
            <div className="pn-card ad-help ad-mt">
              <h3>Meta only sees visits to your site.</h3>
              <p>
                It can't tell when someone submits your form. In Meta Events Manager, select your pixel, then <b>Settings</b> and <b>Open Event Setup Tool</b>. Choose <b>Track new button</b>, tap your form's submit button,
                and pick <b>Lead</b>. No code needed.
              </p>
              <p>
                If your form goes to a thank-you page, choose <b>Track a URL</b> for that page instead.
              </p>
              <a className="pn-btn sm" href={EVENTS_MANAGER} target="_blank" rel="noopener noreferrer">
                Open Events Manager
              </a>
            </div>
          )}
          {step2 && (
            <>
              <div className="pn-cap ad-mt">What Meta aims for</div>
              <div className="pn-grp" role="radiogroup" aria-label="What Meta aims for">
                {learnable.map((e) => (
                  <button key={e.name} type="button" className="pn-row" role="radio" aria-checked={e.name === pick} onClick={() => e.name !== pick && learn(e.name)}>
                    <span className="m">
                      <span className="t">
                        {eventWords(e.name)}
                        {e.name === suggested && <span className="pn-pill acc ad-sugg">Suggested</span>}
                      </span>
                      <span className="s">{e.name}</span>
                    </span>
                    <span className={e.name === pick ? "ad-tick" : "ad-tick off"}>
                      <Icon name="check" />
                    </span>
                  </button>
                ))}
              </div>
              {test?.event && pick !== test.event ? (
                <div className="pn-card ad-help ad-mt">
                  <p>
                    Your test arrived as {eventPlural(test.event).toLowerCase()}, not {eventPlural(pick).toLowerCase()}. Sure Meta should aim for {eventPlural(pick).toLowerCase()}?{slow ? ` ${slow}` : ""}
                  </p>
                  <button type="button" className="pn-btn sm" onClick={() => learn(test.event!)}>
                    Use {eventPlural(test.event).toLowerCase()}
                  </button>
                </div>
              ) : (
                slow && <p className="pn-note">{slow}</p>
              )}
            </>
          )}
          {settings.counts && t?.events && !counted && !test?.visitsOnly && (
            <div className="pn-card ad-help ad-mt">
              <h3>Meta can't see your {eventPlural(settings.counts).toLowerCase()} yet.</h3>
              <p>{settings.counts === "Schedule" ? "Your booking tool" : "The tool that records them"} can send them to Meta, or you can upload them in Meta Events Manager.</p>
              <a className="pn-btn sm" href={EVENTS_MANAGER} target="_blank" rel="noopener noreferrer">
                Open Events Manager
              </a>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
